/**
 * 登録フォーム共通バリデーション
 * Firebase 非依存の純粋関数群
 */

import type { DeviceUserRelation, RegistrationFormData, ValidationError, ValidationOptions } from './types';
import {
  DISPLAY_NAME_MIN_LENGTH,
  DISPLAY_NAME_MAX_LENGTH,
  PET_NAME_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  POSTAL_CODE_LENGTH,
  PHONE_NUMBER_MIN_LENGTH,
  PHONE_NUMBER_MAX_LENGTH,
  CITY_MAX_LENGTH,
  ADDRESS_MAX_LENGTH,
  BUILDING_MAX_LENGTH,
  REFERRAL_CODE_MIN_LENGTH,
  REFERRAL_CODE_MAX_LENGTH,
  USER_RELATION_OTHER_MAX_LENGTH,
  PREFECTURES,
} from './constants';

// =============================================================================
// 個別バリデーション
// =============================================================================

export function validateEmail(email: string): ValidationError | null {
  if (!email || !email.trim()) {
    return { field: 'email', message: 'メールアドレスを入力してください' };
  }
  // 基本的な形式チェック（Firebase Auth が最終的に検証する）
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    return { field: 'email', message: 'メールアドレスの形式が正しくありません' };
  }
  return null;
}

export function validateDisplayName(name: string): ValidationError | null {
  if (!name || name.length < DISPLAY_NAME_MIN_LENGTH || name.length > DISPLAY_NAME_MAX_LENGTH) {
    return {
      field: 'displayName',
      message: `ニックネームは${DISPLAY_NAME_MIN_LENGTH}〜${DISPLAY_NAME_MAX_LENGTH}文字で入力してください`,
    };
  }
  return null;
}

export function validatePetName(name: string): ValidationError | null {
  if (name && name.length > PET_NAME_MAX_LENGTH) {
    return {
      field: 'petName',
      message: `わんちゃんのお名前は${PET_NAME_MAX_LENGTH}文字以内で入力してください`,
    };
  }
  return null;
}

export function validatePassword(password: string): ValidationError | null {
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    return {
      field: 'password',
      message: `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`,
    };
  }
  return null;
}

export function validatePasswordConfirm(password: string, confirm: string): ValidationError | null {
  if (password !== confirm) {
    return { field: 'passwordConfirm', message: 'パスワードが一致しません' };
  }
  return null;
}

export function validatePostalCode(code: string): ValidationError | null {
  const clean = code.replace(/[^0-9]/g, '');
  if (clean.length !== POSTAL_CODE_LENGTH) {
    return {
      field: 'postalCode',
      message: `郵便番号を${POSTAL_CODE_LENGTH}桁で入力してください`,
    };
  }
  return null;
}

export function validatePrefecture(prefecture: string): ValidationError | null {
  if (!prefecture) {
    return { field: 'prefecture', message: '都道府県を選択してください' };
  }
  if (!(PREFECTURES as readonly string[]).includes(prefecture)) {
    return { field: 'prefecture', message: '有効な都道府県を選択してください' };
  }
  return null;
}

export function validateCity(city: string): ValidationError | null {
  if (!city || !city.trim()) {
    return { field: 'city', message: '市区町村を入力してください' };
  }
  if (city.length > CITY_MAX_LENGTH) {
    return { field: 'city', message: `市区町村は${CITY_MAX_LENGTH}文字以内で入力してください` };
  }
  return null;
}

export function validateAddress(address: string): ValidationError | null {
  if (!address || !address.trim()) {
    return { field: 'address', message: '番地を入力してください' };
  }
  if (address.length > ADDRESS_MAX_LENGTH) {
    return { field: 'address', message: `番地は${ADDRESS_MAX_LENGTH}文字以内で入力してください` };
  }
  return null;
}

export function validateBuilding(building: string): ValidationError | null {
  if (building && building.length > BUILDING_MAX_LENGTH) {
    return { field: 'building', message: `建物名は${BUILDING_MAX_LENGTH}文字以内で入力してください` };
  }
  return null;
}

export function validatePhoneNumber(phone: string): ValidationError | null {
  const clean = phone.replace(/[^0-9]/g, '');
  if (clean.length < PHONE_NUMBER_MIN_LENGTH || clean.length > PHONE_NUMBER_MAX_LENGTH) {
    return {
      field: 'phoneNumber',
      message: `電話番号を${PHONE_NUMBER_MIN_LENGTH}〜${PHONE_NUMBER_MAX_LENGTH}桁で入力してください`,
    };
  }
  return null;
}

export function validateUserRelation(
  relation: DeviceUserRelation | '',
  otherText: string,
): ValidationError | null {
  if (!relation) {
    return { field: 'userRelation', message: '使用者との関係を選択してください' };
  }
  if (relation === 'other') {
    if (!otherText || !otherText.trim()) {
      return { field: 'userRelationOther', message: '使用者との関係を入力してください' };
    }
    if (otherText.length > USER_RELATION_OTHER_MAX_LENGTH) {
      return {
        field: 'userRelationOther',
        message: `${USER_RELATION_OTHER_MAX_LENGTH}文字以内で入力してください`,
      };
    }
  }
  return null;
}

export function validateLegalAgreement(
  privacyAgreed: boolean,
  termsAgreed: boolean,
): ValidationError | null {
  if (!privacyAgreed) {
    return { field: 'agreedToPrivacyPolicy', message: 'プライバシーポリシーに同意してください' };
  }
  if (!termsAgreed) {
    return { field: 'agreedToTermsOfService', message: '利用規約に同意してください' };
  }
  return null;
}

export function validateCrossBorderConsent(
  agreed: boolean,
): ValidationError | null {
  if (!agreed) {
    return {
      field: 'agreedToCrossBorderTransfer',
      message: '個人データの外国にある第三者への提供に同意してください',
    };
  }
  return null;
}

export function validateReferralCode(
  code: string,
  isValid: boolean | null,
): ValidationError | null {
  if (!code) return null; // 空なら問題なし
  if (code.length < REFERRAL_CODE_MIN_LENGTH) {
    return { field: 'referralCode', message: `紹介コードは${REFERRAL_CODE_MIN_LENGTH}文字以上です` };
  }
  if (code.length > REFERRAL_CODE_MAX_LENGTH) {
    return { field: 'referralCode', message: `紹介コードは${REFERRAL_CODE_MAX_LENGTH}文字以内です` };
  }
  if (isValid === false) {
    return {
      field: 'referralCode',
      message: '紹介コードが無効です。正しいコードを入力するか、空欄にしてください。',
    };
  }
  return null;
}

// =============================================================================
// 端末申込フィールド一括バリデーション
// =============================================================================

export function validateDeviceApplication(data: RegistrationFormData): ValidationError[] {
  if (!data.wantsDevice) return [];

  const errors: ValidationError[] = [];
  const checks = [
    validatePostalCode(data.postalCode),
    validatePrefecture(data.prefecture),
    validateCity(data.city),
    validateAddress(data.address),
    validateBuilding(data.building),
    validatePhoneNumber(data.phoneNumber),
    validateUserRelation(data.userRelation, data.userRelationOther),
  ];

  for (const err of checks) {
    if (err) errors.push(err);
  }

  return errors;
}

// =============================================================================
// 全フィールド一括バリデーション
// =============================================================================

export function validateRegistrationForm(
  data: RegistrationFormData,
  options: ValidationOptions = {},
): ValidationError[] {
  const errors: ValidationError[] = [];

  const push = (err: ValidationError | null) => {
    if (err) errors.push(err);
  };

  // 必須
  push(validateEmail(data.email));
  push(validateDisplayName(data.displayName));
  push(validateLegalAgreement(data.agreedToPrivacyPolicy, data.agreedToTermsOfService));
  push(validateCrossBorderConsent(data.agreedToCrossBorderTransfer));

  // パスワード（Web App のみ）
  if (options.requirePassword) {
    push(validatePassword(options.password || ''));
    push(validatePasswordConfirm(options.password || '', options.passwordConfirm || ''));
  }

  // 任意
  push(validateReferralCode(data.referralCode, data.referralValid));

  // 端末申込
  errors.push(...validateDeviceApplication(data));

  return errors;
}

// =============================================================================
// 入力サニタイザー
// =============================================================================

export function sanitizePostalCode(input: string): string {
  return input.replace(/[^0-9]/g, '').slice(0, POSTAL_CODE_LENGTH);
}

export function sanitizePhoneNumber(input: string): string {
  return input.replace(/[^0-9]/g, '').slice(0, PHONE_NUMBER_MAX_LENGTH);
}

export function sanitizeReferralCode(input: string): string {
  return input.toUpperCase().slice(0, REFERRAL_CODE_MAX_LENGTH);
}

export function sanitizeDisplayName(input: string): string {
  return input.slice(0, DISPLAY_NAME_MAX_LENGTH);
}
